import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { AdminCheckbox } from "./_ui";

const fmt$ = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;
const fmtDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

/**
 * Two-step backfill: preview every paid gift not yet on the wall, then confirm.
 * `client` is the caller's authed axios instance; `onDone` refreshes the table.
 */
export const FoundingBackfillDialog = ({ open, onOpenChange, client, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setError("");
    setSendWelcome(true);
    client
      .post("/admin/founding/sync-donations", { dry_run: true })
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't preview past gifts."));
  }, [open, client]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await client.post("/admin/founding/sync-donations", { dry_run: false, send_welcome: sendWelcome });
      const rows = r.data?.rows || [];
      const emailed = rows.filter((x) => x.emailed).length;
      toast.success(
        rows.length ? `${rows.length} ${rows.length === 1 ? "donor" : "donors"} added to the wall.` : "Nothing to add — the wall is already up to date.",
        { description: emailed ? `${emailed} welcome ${emailed === 1 ? "letter" : "letters"} sent with a private edit link.` : undefined }
      );
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add past gifts.");
    } finally {
      setBusy(false);
    }
  };

  const rows = preview?.rows || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="founding-backfill-dialog">
        <DialogHeader>
          <DialogTitle>Add past gifts to the wall</DialogTitle>
          <DialogDescription>
            Every paid gift on record that isn&rsquo;t on the Founding Donors wall yet. Nothing is written until you confirm.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="rounded-md border border-scarlet/25 bg-scarlet/[0.05] p-3 text-[13px] text-scarlet" data-testid="founding-backfill-error">{error}</p>
        ) : preview === null ? (
          <div className="flex items-center justify-center py-10" data-testid="founding-backfill-loading">
            <Loader2 className="h-5 w-5 animate-spin text-olive" />
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-border bg-sand/40 p-4 text-center text-[13px] text-olive" data-testid="founding-backfill-empty">
            The wall is already up to date. {preview.skipped ? `${preview.skipped} paid ${preview.skipped === 1 ? "gift is" : "gifts are"} already listed.` : ""}
          </p>
        ) : (
          <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border bg-sand/40" data-testid="founding-backfill-rows">
            {rows.map((r, i) => (
              <li key={`${r.email || r.full_name}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-[13px]" data-testid={`founding-backfill-row-${i}`}>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink">
                    {r.full_name || "—"}
                    {r.anonymous && <span className="ml-2 text-[10px] font-medium uppercase tracking-[0.14em] text-olive">shown anon</span>}
                  </p>
                  <p className="truncate text-[11px] text-olive">{r.email || <span className="italic">no email</span>} · {fmtDate(r.joined_date)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-mono text-ink">{fmt$(r.amount_cents)}</p>
                  <p className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${r.action === "added" ? "text-grass" : "text-cobalt"}`}>
                    {r.action === "added" ? "new entry" : "joins existing entry"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {rows.length > 0 && (
          <AdminCheckbox
            checked={sendWelcome}
            onChange={setSendWelcome}
            testid="founding-backfill-send-welcome"
            label="Email each new donor the founding-donor welcome letter"
            hint="Includes their private 30-day link to add a photo, alma mater or tribute, or go anonymous."
          />
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="founding-backfill-cancel">
            {rows.length ? "Cancel" : "Close"}
          </Button>
          {rows.length > 0 && (
            <Button type="button" onClick={confirm} disabled={busy} data-testid="founding-backfill-confirm">
              {busy ? "Adding…" : `Add ${rows.length} to the wall`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
